import { useEffect, useRef } from 'react';

const ARROW_SPACING = 30;
const ARROW_LENGTH = 13;

// On terrain maps (light background): solid dark arrows
// On satellite/hybrid (green vegetation): white arrows with dark outline for contrast
function drawArrow(ctx, x, y, len, angleRad, mapType) {
  const headLen = len * 0.38;

  const paintArrow = (strokeColor, lineWidth) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angleRad);
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(0, -len / 2);
    ctx.lineTo(0, len / 2);
    ctx.moveTo(-headLen * 0.55, len / 2 - headLen);
    ctx.lineTo(0, len / 2);
    ctx.lineTo(headLen * 0.55, len / 2 - headLen);
    ctx.stroke();
    ctx.restore();
  };

  if (mapType === 'terrain') {
    paintArrow('rgba(40, 40, 40, 0.82)', 1.8);
  } else {
    // satellite / hybrid: dark outline first, then white on top
    paintArrow('rgba(0, 0, 0, 0.65)', 3.5);
    paintArrow('rgba(255, 255, 255, 0.92)', 1.6);
  }
}

const useArrowTextureOverlay = (map, maps, parcelaPolygon, angle, mapType) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!map || !maps || !parcelaPolygon) return;

    if (overlayRef.current) {
      overlayRef.current.setMap(null);
      overlayRef.current = null;
    }

    let poligonoData;
    try {
      const parsed = JSON.parse(parcelaPolygon);
      poligonoData = parsed.coordinates[0].map(p => ({ lat: p[1], lng: p[0] }));
    } catch {
      return;
    }

    const bounds = new maps.LatLngBounds();
    poligonoData.forEach(p => bounds.extend(new maps.LatLng(p.lat, p.lng)));

    const angleRad = (((angle || 0) + 180) * Math.PI) / 180;

    class ArrowOverlay extends maps.OverlayView {
      constructor() {
        super();
        this._div = null;
        this._canvas = null;
      }

      onAdd() {
        this._div = document.createElement('div');
        this._div.style.position = 'absolute';
        this._canvas = document.createElement('canvas');
        this._canvas.style.position = 'absolute';
        this._div.appendChild(this._canvas);
        this.getPanes().overlayLayer.appendChild(this._div);
      }

      draw() {
        const proj = this.getProjection();
        if (!proj) return;

        const sw = proj.fromLatLngToDivPixel(bounds.getSouthWest());
        const ne = proj.fromLatLngToDivPixel(bounds.getNorthEast());

        const w = Math.round(ne.x - sw.x);
        const h = Math.round(sw.y - ne.y);
        if (w <= 0 || h <= 0) return;

        this._div.style.left = `${sw.x}px`;
        this._div.style.top = `${ne.y}px`;
        this._div.style.width = `${w}px`;
        this._div.style.height = `${h}px`;

        if (this._canvas.width !== w || this._canvas.height !== h) {
          this._canvas.width = w;
          this._canvas.height = h;
        }

        const ctx = this._canvas.getContext('2d');
        ctx.clearRect(0, 0, w, h);

        ctx.save();
        ctx.beginPath();
        poligonoData.forEach((p, i) => {
          const px = proj.fromLatLngToDivPixel(new maps.LatLng(p.lat, p.lng));
          const x = px.x - sw.x;
          const y = px.y - ne.y;
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        });
        ctx.closePath();
        ctx.clip();

        for (let x = ARROW_SPACING / 2; x < w; x += ARROW_SPACING) {
          for (let y = ARROW_SPACING / 2; y < h; y += ARROW_SPACING) {
            drawArrow(ctx, x, y, ARROW_LENGTH, angleRad, mapType);
          }
        }

        ctx.restore();
      }

      onRemove() {
        if (this._div && this._div.parentNode) {
          this._div.parentNode.removeChild(this._div);
          this._div = null;
          this._canvas = null;
        }
      }
    }

    const overlay = new ArrowOverlay();
    overlay.setMap(map);
    overlayRef.current = overlay;

    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
    };
  }, [map, maps, parcelaPolygon, angle, mapType]);
};

export default useArrowTextureOverlay;
